import { LoanApplication, CreditScoreConfig, Rule, AdvancedCreditScore, RiskFactor } from '../types';
import FraudDetectionService from './fraudDetectionService';

interface ScoreResult {
  score: number;
  status: 'approved' | 'rejected' | 'pending';
  interestRate?: number;
}

interface AdvancedScoreResult extends ScoreResult {
  advancedScore: AdvancedCreditScore;
  riskFactors: RiskFactor[];
  fraudDetectionResult?: any;
}

const evaluateRule = (application: LoanApplication, rule: Rule): boolean => {
  let applicantValue: any;

  switch (rule.field as keyof LoanApplication) {
    case 'monthlyIncome':
      applicantValue = application.monthlyIncome;
      break;
    case 'age':
      applicantValue = application.age;
      break;
    case 'employmentDuration':
      applicantValue = application.employmentDuration;
      break;
    case 'existingLoans':
        applicantValue = application.existingLoans;
        break;
    case 'yearsInBusiness':
        applicantValue = application.yearsInBusiness || 0;
        break;
    default:
        // Handle custom fields if any, for now, we assume direct mapping
        if (rule.field in application) {
            applicantValue = (application as any)[rule.field];
        } else {
            return false;
        }
  }

  const ruleValue = Number(rule.value);

  switch (rule.operator) {
    case '>':
      return applicantValue > ruleValue;
    case '>=':
      return applicantValue >= ruleValue;
    case '<':
      return applicantValue < ruleValue;
    case '<=':
      return applicantValue <= ruleValue;
    case '===':
      return applicantValue === ruleValue;
    default:
      return false;
  }
};

export const calculateCreditScore = (
  application: LoanApplication,
  config: CreditScoreConfig
): ScoreResult => {
  let score = config.baseScore;
  const rules = application.customerType === 'sme' ? [...config.rules, ...config.smeRules] : config.rules;

  // Rule-based scoring
  for (const rule of rules) {
    if (evaluateRule(application, rule)) {
      score += rule.score;
    } else {
      // Optional: penalty for not meeting a rule
      // score -= Math.abs(rule.score) / 2;
    }
  }

  // Ensure score stays within 300-900 range
  score = Math.max(300, Math.min(score, 900));

  let status: 'approved' | 'rejected' = 'approved';
  let interestRate: number | undefined = undefined;

  // Basic checks from config
  if (application.monthlyIncome < config.minMonthlyIncome) {
    return { score, status: 'rejected' };
  }
  if (application.requestedAmount < config.minLoanAmount || application.requestedAmount > config.maxLoanAmount) {
    return { score, status: 'rejected' };
  }

  // Determine interest rate based on score
  if (score >= 800) {
    interestRate = config.interestRates['800+'];
  } else if (score >= 700) {
    interestRate = config.interestRates['700-799'];
  } else if (score >= 600) {
    interestRate = config.interestRates['600-699'];
  } else if (score >= 500) {
    interestRate = config.interestRates['500-599'];
  } else {
    status = 'rejected';
  }

  if (status === 'approved' && interestRate === undefined) {
      // Fallback for edge cases where score is in an uncovered range but status is approved
      status = 'rejected';
  }

  return { score, status, interestRate };
};

/**
 * Advanced credit scoring with fraud detection and risk assessment
 */
export const calculateAdvancedCreditScore = async (
  application: LoanApplication,
  config: CreditScoreConfig
): Promise<AdvancedScoreResult> => {
  // Initialize fraud detection service
  const fraudDetectionService = new FraudDetectionService();
  
  // Get traditional score
  const traditionalResult = calculateCreditScore(application, config);
  
  // Perform fraud detection
  const fraudDetectionResult = await fraudDetectionService.detectFraud(application);
  
  // Calculate behavioral score
  const behavioralScore = calculateBehavioralScore(application);
  
  // Calculate alternative data score
  const alternativeDataScore = calculateAlternativeDataScore(application);
  
  // Calculate social score
  const socialScore = calculateSocialScore(application);
  
  // Calculate fraud risk score (inverted - higher fraud = lower score)
  const fraudRiskScore = Math.max(0, 100 - fraudDetectionResult.fraudScore);
  
  // Calculate final score using weighted combination
  const finalScore = calculateWeightedScore({
    traditional: traditionalResult.score,
    behavioral: behavioralScore,
    social: socialScore,
    alternativeData: alternativeDataScore,
    fraudRisk: fraudRiskScore,
  });
  
  // Generate risk factors
  const riskFactors = fraudDetectionService.generateRiskFactors(application, fraudDetectionResult);
  
  // Determine status based on advanced scoring
  let status: 'approved' | 'rejected' | 'pending' = 'approved';
  let interestRate: number | undefined = undefined;
  
  if (fraudDetectionResult.recommendedAction === 'reject') {
    status = 'rejected';
  } else if (fraudDetectionResult.recommendedAction === 'investigate') {
    status = 'pending';
  } else if (finalScore < 500) {
    status = 'rejected';
  } else {
    // Calculate interest rate based on final score
    if (finalScore >= 800) {
      interestRate = config.interestRates['800+'] || 5;
    } else if (finalScore >= 700) {
      interestRate = config.interestRates['700-799'] || 8;
    } else if (finalScore >= 600) {
      interestRate = config.interestRates['600-699'] || 12;
    } else if (finalScore >= 500) {
      interestRate = config.interestRates['500-599'] || 18;
    }
  }
  
  // Create advanced score object
  const advancedScore: AdvancedCreditScore = {
    traditionalScore: traditionalResult.score,
    behavioralScore,
    socialScore,
    alternativeDataScore,
    fraudRiskScore,
    finalScore,
    confidenceLevel: fraudDetectionResult.confidence,
    riskFactors,
    recommendedActions: generateRecommendedActions(fraudDetectionResult, riskFactors),
    scoringModel: 'hybrid',
  };
  
  return {
    score: finalScore,
    status,
    interestRate,
    advancedScore,
    riskFactors,
    fraudDetectionResult,
  };
};

/**
 * Calculate behavioral score based on application patterns
 */
const calculateBehavioralScore = (application: LoanApplication): number => {
  let score = 0;
  
  // Employment stability
  if (application.employmentDuration >= 60) score += 100; // 5+ years
  else if (application.employmentDuration >= 24) score += 80; // 2+ years
  else if (application.employmentDuration >= 12) score += 60; // 1+ year
  else if (application.employmentDuration >= 6) score += 40; // 6+ months
  else score += 20; // Less than 6 months
  
  // Age stability
  if (application.age >= 30 && application.age <= 50) score += 50; // Prime age
  else if (application.age >= 25 && application.age <= 55) score += 30; // Good age range
  else score += 10; // Other ages
  
  // Income stability
  if (application.monthlyIncome >= 1000000) score += 50; // High income
  else if (application.monthlyIncome >= 500000) score += 30; // Medium income
  else score += 20; // Lower income
  
  // Debt burden
  if (application.existingLoans === 0) score += 50; // No existing debt
  else if (application.existingLoans <= 2) score += 30; // Manageable debt
  else if (application.existingLoans <= 4) score += 10; // High debt
  else score -= 20; // Very high debt
  
  return Math.max(0, Math.min(100, score));
};

/**
 * Calculate alternative data score
 */
const calculateAlternativeDataScore = (application: LoanApplication): number => {
  let score = 50; // Base score
  
  // Check if KYC data is complete
  if (application.kycData?.compliance) {
    const compliance = application.kycData.compliance;
    
    if (compliance.kycVerification === 'verified') score += 20;
    if (compliance.documentVerification === 'verified') score += 20;
    if (compliance.addressVerification === 'verified') score += 15;
    if (compliance.employmentVerification === 'verified') score += 15;
    if (compliance.bankVerification === 'verified') score += 10;
  }
  
  // Check business information for SMEs
  if (application.customerType === 'sme' && application.kycData?.businessInfo) {
    const businessInfo = application.kycData.businessInfo;
    
    if (businessInfo.yearsInBusiness >= 5) score += 20;
    if (businessInfo.annualRevenue >= 1000000000) score += 15;
    if (businessInfo.numberOfEmployees >= 10) score += 10;
  }
  
  return Math.max(0, Math.min(100, score));
};

/**
 * Calculate social score
 */
const calculateSocialScore = (application: LoanApplication): number => {
  let score = 50; // Base score
  
  // Email domain quality
  const emailDomain = application.email.split('@')[1];
  const professionalDomains = ['gmail.com', 'yahoo.com', 'outlook.com', 'company.com'];
  
  if (professionalDomains.includes(emailDomain)) score += 20;
  else if (emailDomain.includes('.edu')) score += 30; // Educational
  else if (emailDomain.includes('.gov')) score += 25; // Government
  else score += 10; // Other domains
  
  // Phone number quality
  const phone = application.phone.replace(/\D/g, '');
  if (phone.length >= 10 && phone.length <= 15) score += 20;
  else score += 10;
  
  // National ID validation
  if (application.nationalId && application.nationalId.length >= 8) score += 10;
  
  return Math.max(0, Math.min(100, score));
};

/**
 * Calculate weighted final score
 */
const calculateWeightedScore = (scores: {
  traditional: number;
  behavioral: number;
  social: number;
  alternativeData: number;
  fraudRisk: number;
}): number => {
  const weights = {
    traditional: 0.35,
    behavioral: 0.25,
    social: 0.15,
    alternativeData: 0.15,
    fraudRisk: 0.10,
  };
  
  const weightedSum = 
    scores.traditional * weights.traditional +
    scores.behavioral * weights.behavioral +
    scores.social * weights.social +
    scores.alternativeData * weights.alternativeData +
    scores.fraudRisk * weights.fraudRisk;
  
  return Math.round(weightedSum);
};

/**
 * Generate recommended actions based on fraud detection and risk factors
 */
const generateRecommendedActions = (fraudResult: any, riskFactors: RiskFactor[]): string[] => {
  const actions: string[] = [];
  
  if (fraudResult.recommendedAction === 'investigate') {
    actions.push('Enhanced due diligence required');
    actions.push('Additional document verification needed');
    actions.push('Manual review by senior underwriter');
  }
  
  if (fraudResult.recommendedAction === 'review') {
    actions.push('Standard review process');
    actions.push('Verify key information');
  }
  
  // Add risk-specific actions
  riskFactors.forEach(factor => {
    if (factor.impact === 'high') {
      actions.push(`Address ${factor.factor}: ${factor.mitigation}`);
    }
  });
  
  return actions;
}; 